import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/lib/api';

const PushNotifications = ({ user }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState('default');
  const autoSubscribeAttempted = useRef(false);

  useEffect(() => {
    checkSupport();
  }, []);

  useEffect(() => {
    if (user && isSupported) {
      checkSubscriptionStatus();
    }
  }, [user, isSupported]);

  // Auto-subscribe when user logs in
  useEffect(() => {
    if (user && isSupported && !isSubscribed && !loading && !autoSubscribeAttempted.current) {
      autoSubscribeAttempted.current = true;
      // Small delay to let the page load first
      const timer = setTimeout(() => {
        autoSubscribe();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, isSupported, isSubscribed, loading]);

  const checkSupport = () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
    }
    setLoading(false);
  };

  const checkSubscriptionStatus = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) return;
      
      const res = await axios.get(`${API}/push/status`, { 
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setIsSubscribed(res.data.subscribed);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const autoSubscribe = async () => {
    // Don't auto-subscribe if already subscribed or permission denied
    if (isSubscribed || permission === 'denied') return;
    
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        // User denied - silently fail, they can enable later in browser settings
        console.log('Notification permission not granted');
        return;
      }

      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        console.log('No session token, skipping push subscription');
        return;
      }

      // Get VAPID public key
      const vapidRes = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidRes.data.publicKey;

      if (!vapidPublicKey) {
        console.error('No VAPID public key available');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend with auth header
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON()
      }, { 
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      setIsSubscribed(true);
      toast.success('Notificações ativadas! Você receberá lembretes importantes.');

    } catch (error) {
      console.error('Error auto-subscribing:', error);
      // Silently fail - notifications are a nice-to-have
    }
  };

  // Only show status indicator, no toggle button
  if (!isSupported || !user) {
    return null;
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-2 text-yellow-500/80 text-xs bg-yellow-500/10 px-3 py-1.5 rounded-full">
        <Bell className="w-3 h-3" />
        <span>Ative notificações nas configurações do navegador</span>
      </div>
    );
  }

  if (isSubscribed) {
    return (
      <div className="flex items-center gap-2 text-[#00FF94]/80 text-xs bg-[#00FF94]/10 px-3 py-1.5 rounded-full">
        <Check className="w-3 h-3" />
        <span>Lembretes ativos</span>
      </div>
    );
  }

  // Show loading or nothing while auto-subscribing
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-xs">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Configurando...</span>
      </div>
    );
  }

  // Not subscribed yet - show button to manually subscribe
  if (!isSubscribed && permission !== 'denied') {
    return (
      <button
        onClick={autoSubscribe}
        className="flex items-center gap-2 text-[#00FF94]/80 text-xs bg-[#00FF94]/10 px-3 py-1.5 rounded-full hover:bg-[#00FF94]/20 transition-colors"
      >
        <Bell className="w-3 h-3" />
        <span>Ativar lembretes</span>
      </button>
    );
  }

  return null;
};

export default PushNotifications;
